<template lang="pug">
  div(
    class="document-share-pro"
    data-test="document-share-pro-modal"
  )
    div(class="document-share-pro__header-section")
      div(class="document-share-pro__header")
        h2(class="title-32px bold") {{ $t('Share this document') }}
        v-btn(
          icon
          small
          text
          color="grey darken-1"
          @click="closeDialogOrDrawer(false)"
          data-test="document-share-pro-close-btn"
        )
          v-icon(size="20") mdi-close

    div(class="document-share-pro__main-content")
      section(class="document-share-pro__content")
        div(
          v-if="viewMode === 'loading'"
          class="document-share-pro__loading"
        )
          e-progress-circular(
            size="lg"
            width="4"
            color="primary"
          )
        div(
          v-else-if="viewMode === 'recipients'"
          class="document-share-pro__recipients"
        )
          div(class="document-share-pro__input-section")
            m-document-share-add-participant-form(
              :document-id="document.id"
              :formatted-actions="formattedParticipantActions"
              :existing-participants="filteredParticipants"
              :current-user-email="$User.email"
              @participant-added="handleParticipantAdded"
            )

          div(
            v-if="participantsLoading"
            class="d-flex align-center justify-center pa-4"
          )
            e-progress-circular(
              size="lg"
              width="4"
              color="primary"
            )

          div(
            v-else-if="filteredParticipants.length > 0"
            class="document-share-pro__participants-wrapper"
          )
            div(class="document-share-pro__participants")
              div(class="document-share-pro__participants-header")
                div(class="text-16px dark") {{ $t('Users with access') }} ({{ filteredParticipants.length }})
                v-btn(
                  v-if="showReorderButton"
                  text
                  small
                  class="secondary-button text-btn document-share-pro__change-order-btn"
                  data-test="document-share-pro-change-order-btn"
                  @click="startReorderMode"
                ) {{ $t('Change order') }}
                v-btn(
                  v-else-if="isReorderMode"
                  text
                  small
                  class="secondary-button text-btn"
                  data-test="document-share-pro-save-order-btn"
                  @click="saveReorderChanges"
                ) {{ $t('Save changes') }}

            template(v-if="!isReorderMode")
              block-document-share-participant-item(
                v-for="(participant, idx) in filteredParticipants"
                :key="participant.localId"
                :participant="participant"
                :index="idx"
                :is-drag-mode="false"
                :show-number="true"
                :actions="formattedParticipantActions"
                :current-action="getParticipantCurrentAction(participant)"
                :loading="participantsDeleteLoading[participant.id]"
                :disabled="!canEditParticipant(participant)"
                data-test="document-share-participant-action-select"
                @action-change="handleParticipantActionChange(participant, $event)"
                @delete-participant="deleteParticipant(participant)"
              )
            template(v-else)
              Draggable(
                v-model="draggableParticipants"
                handle=".draggable"
                v-bind="dragOptions"
                :draggable="'.draggable-enabled'"
                @start="drag = true"
                @end="onFlipParticipantsEnd"
              )
                transition-group(
                  type="transition"
                  :name="!drag ? 'flip-list' : null"
                )
                  block-document-share-participant-item(
                    v-for="(participant, idx) in draggableParticipants"
                    :key="participant.localId || participant.id"
                    :participant="participant"
                    :index="idx"
                    :is-drag-mode="true"
                    :show-number="false"
                    :disabled="!canEditParticipant(participant)"
                    :action-label="getCurrentActionLabel(participant)"
                  )
        div(
          v-else-if="viewMode === 'public'"
          class="document-share-pro__public"
        )
          div(class="document-share-pro__input-section")
            div(
              v-if="publicLinkUrl || copyLoading"
              class="document-share-pro__public-link-container"
            )
              div(class="document-share-pro__public-link-url")
                span(class="document-share-pro__public-link-text") {{ publicLinkUrl || $t('Generating link...') }}
                e-svg-icon(name="copy")
              v-btn(
                class="document-share-pro__copy-button"
                outlined
                :disabled="copyLoading || !publicLinkUrl"
                :loading="copyLoading"
                data-test="document-share-pro-copy-link-btn"
                @click="copyPublicLink"
              ) {{ $t('Copy') }}
          div(
            class="document-share-pro__public-link-actions"
            data-test="document-share-pro-public-link-section"
          )
            div(class="document-share-pro__participants-add")
              div(class="document-share-pro__participants-icon")
                e-svg-icon(name="globe")
              div(class="document-share-pro__participants-content")
                div(class="text-16px dark") {{ $t('Public link') }}
                div(class="text-14px light") {{ $t('Users who have this link can') }}
            div(class="document-share-pro__participants-add-action")
              e-unified-select(
                v-model="publicAction"
                :items="publicActions"
                custom-class="document-share-pro__public-action-select"
                data-test="document-form-public-action-select"
              )

    div(class="document-share-pro__footer")
      div(class="document-share-pro__footer-left")
        div(class="document-share-pro__public-link-toggle")
          v-switch(
            v-model="enablePublicLink"
            @change="handlePublicLinkToggle"
            hide-details
            class="mt-0 pt-0"
          )
          span(class="text-14px dark ml-2") {{ $t('Public link') }}
      div(class="document-share-pro__footer-right")
        v-btn(
          height="48"
          data-test="document-share-pro-cancel-btn"
          @click="closeModal(false)"
        ) {{ $t('Cancel') }}
        v-btn(
          class="main-button"
          :loading="viewMode === 'loading'"
          :disabled="disableSubmit"
          data-test="document-share-pro-submit-btn"
          @click="handleSubmit"
        ) {{ submitButtonText }}
</template>

<script>
import { mapGetters } from 'vuex'
import Draggable from 'vuedraggable'
import EProgressCircular from '~/components/elements/progress/e-progress-circular'
import EUnifiedSelect from '~/components/elements/selections/e-unified-select'
import BlockDocumentShareParticipantItem from '~/modules/documents/components/blocks/block-document-share-participant-item'
import MDocumentShareAddParticipantForm from '~/modules/documents/components/modules/m-document-share-add-participant-form'
import clipboard from '~/mixins/global/_clipboard'
import responsive from '~/mixins/pages/responsive'
import participants from '~/modules/documents/mixins/participants'
import { DOCUMENT_STATES } from '~/modules/documents/constants/documents'
import { PARTICIPANTS_ROLES } from '~/modules/documents/constants/participants'
import contentDialogContentTop from '~/mixins/dialogs/contentDialogContentTop'
import contentDialog from '~/mixins/dialogs/contentDialog'
import { isSafari } from '~/services/_utils/utils'

export default {
  name: 'MDocumentSharePro',
  components: {
    Draggable,
    EProgressCircular,
    EUnifiedSelect,
    BlockDocumentShareParticipantItem,
    MDocumentShareAddParticipantForm
  },
  mixins: [contentDialogContentTop, contentDialog, participants, clipboard, responsive],
  props: {
    document: {
      type: Object,
      default: null
    },
    closeModal: {
      type: Function,
      default: () => {}
    },
    closeDrawer: {
      type: Function,
      default: () => {}
    }
  },
  data() {
    return {
      publicAction: 'sign',
      newUsers: [],
      participantsLoading: false,
      copyLoading: false,
      participantsDeleteLoading: {},
      hasCopiedLink: false,
      requiredOwnerSignature: true,
      viewMode: 'recipients',
      enablePublicLink: false,
      publicLinkUrl: '',
      drag: false,
      isReorderMode: false,
      localParticipantChanges: {},
      localParticipantsOrder: {},
      localDeletedParticipants: {},
      loadedDocument: null
    }
  },
  computed: {
    ...mapGetters('participants', {
      participants: 'items',
      owner: 'owner'
    }),

    isOwnerSignatureRequired() {
      return this.owner?.isSignatureRequired || false
    },

    disableSubmit() {
      if (this.isDocumentSent) {
        return false
      }
      if (this.enablePublicLink) {
        return !this.hasCopiedLink
      }
      return this.filteredParticipants.length === 0
    },

    currentDocument() {
      return this.loadedDocument || this.document
    },

    hasDocumentFields() {
      return !!(
        this.currentDocument?.canFillFields ||
        (this.currentDocument?.fields && this.currentDocument.fields.length > 0)
      )
    },

    formattedParticipantActions() {
      return this.hasDocumentFields
        ? this.participantActions
        : this.participantActions.filter(action => action.value !== this.participantActionsMap.FILL)
    },

    isDocumentSent() {
      return [DOCUMENT_STATES.SENT, DOCUMENT_STATES.SIGNED].includes(this.loadedDocument?.state)
    },

    filteredParticipants() {
      const existingParticipants = this.participants
        .filter(p => !this.localDeletedParticipants[p.id])
        .map(p => {
          const localChange = this.localParticipantChanges[p.id]
          const localOrderChange = this.localParticipantsOrder[p.id]
          return {
            id: p.id,
            localId: p.id,
            emailOrEdrpou: p.user?.email || p.edrpou || p.organization?.edrpou || '',
            displayName: this.getParticipantName(p),
            role: localChange ? localChange.role : this.participantsRoleActionMap[p.role] || p.role,
            priority: localOrderChange !== undefined ? localOrderChange : p.priority || 0,
            status: p.status,
            isExisting: true,
            changedRole: localChange?.changedRole || false
          }
        })
      const newParticipants = this.newUsers.map((user, idx) => {
        const localOrderChange = this.localParticipantsOrder[user.id]
        return {
          id: user.id,
          localId: user.id,
          emailOrEdrpou: user.email || user.edrpou || '',
          displayName: user.name || user.email || `Організація ${user.edrpou}`,
          action: user.action,
          role:
            user.role ||
            Object.keys(this.participantsRoleActionMap).find(
              role => this.participantsRoleActionMap[role] === user.action
            ) ||
            PARTICIPANTS_ROLES.SIGNER,
          priority:
            localOrderChange !== undefined ? localOrderChange : existingParticipants.length + idx,
          isNew: true
        }
      })
      const ownerParticipant = this.owner
        ? [
            {
              id: this.owner.id,
              localId: this.owner.id,
              emailOrEdrpou:
                this.owner.user?.email || this.owner.edrpou || this.$User?.email || 'Ви',
              displayName: 'Ви (Власник)',
              role: this.owner.role,
              priority: -1,
              status: this.owner.status,
              isOwner: true
            }
          ]
        : []

      return [...ownerParticipant, ...existingParticipants, ...newParticipants]
        .filter(p => p.emailOrEdrpou)
        .sort((a, b) => (a.priority || 0) - (b.priority || 0))
    },
    filteredParticipantsWithoutOwner() {
      return this._.filter(this.filteredParticipants, p => !p.isOwner)
    },
    dragOptions: () => ({
      animation: 200,
      group: 'description',
      disabled: false,
      ghostClass: 'block-document-share-participant-item--active'
    }),

    draggableParticipants: {
      get() {
        return this.filteredParticipants.slice()
      },
      set(newOrder) {
        newOrder.forEach((participant, idx) => {
          if (!participant.isOwner) {
            this.$set(this.localParticipantsOrder, participant.id, idx)
          }
        })
      }
    },

    showReorderButton() {
      const editableParticipants = this.filteredParticipants.filter(
        p => this.canEditParticipant(p) && !p.isOwner
      )
      return !this.isReorderMode && editableParticipants.length > 1
    },

    publicActions() {
      const allowedValues = this.hasDocumentFields
        ? [
            this.participantActionsMap.SIGN,
            this.participantActionsMap.VIEW,
            this.participantActionsMap.FILL,
            this.participantActionsMap.APPROVE
          ]
        : [
            this.participantActionsMap.SIGN,
            this.participantActionsMap.VIEW,
            this.participantActionsMap.APPROVE
          ]
      return this.participantActions.filter(action => allowedValues.includes(action.value))
    },

    submitButtonText() {
      if (this.isDocumentSent) return this.$t('Save')
      return this.enablePublicLink ? this.$t('Ready') : this.$t('Send')
    }
  },
  watch: {
    enablePublicLink(val) {
      this.viewMode = val ? 'public' : 'recipients'
      this.deleteAllParticipants()
    },

    async publicAction(newAction, oldAction) {
      if (this.enablePublicLink && this.publicLinkUrl && newAction !== oldAction) {
        await this.updatePublicLinkAction()
      }
    }
  },
  async created() {
    if (this.document?.id && !this.document?.fields) {
      try {
        this.loadedDocument = await this.$store.dispatch('documents/read', { id: this.document.id })
      } catch (e) {
        this.loadedDocument = null
      }
    }
    await this.getParticipants()

    this.enablePublicLink = this.document?.hasPublicAccess || false
    this.viewMode = this.enablePublicLink ? 'public' : 'recipients'

    if (this.document?.publicAccessType) {
      this.publicAction = this.document.publicAccessType
    }

    this.participants.forEach(participant => {
      this.$set(this.participantsDeleteLoading, participant.id, false)
    })

    if (this.enablePublicLink) {
      await this.generatePublicLinkForDisplay()
    }
  },
  mounted() {
    window.dataLayer &&
      window.dataLayer.push({
        event: 'publicpage_view',
        email: this.$User?.email
      })
  },
  methods: {
    deleteAllParticipants() {
      this.filteredParticipantsWithoutOwner.forEach(participant => {
        this.deleteParticipant(participant)
      })
    },
    async getParticipants() {
      try {
        this.participantsLoading = true
        await this.$store.dispatch('participants/getAllDocumentParticipantsWithOwner', {
          documentId: this.document.id
        })
        this.requiredOwnerSignature = this.owner?.isSignatureRequired || false
      } catch (e) {
        this.$handlers.error(e, this)
      } finally {
        this.participantsLoading = false
      }
    },
    async handleSubmit() {
      this.viewMode = 'loading'
      try {
        const { occurredError, hasUpdates } = await this.processAllChanges()
        await this.handleSubmitResult({ occurredError, hasUpdates })
      } finally {
        this.viewMode = this.enablePublicLink ? 'public' : 'recipients'
      }
    },
    async processAllChanges() {
      let occurredError = false
      let hasUpdates = false

      const ownerResult = await this.updateOwnerSignature()
      occurredError = occurredError || ownerResult.error
      hasUpdates = hasUpdates || ownerResult.updated

      const deletedResult = await this.processDeletedParticipants()
      occurredError = occurredError || deletedResult.error
      hasUpdates = hasUpdates || deletedResult.updated

      const changedResult = await this.processChangedParticipants()
      occurredError = occurredError || changedResult.error
      hasUpdates = hasUpdates || changedResult.updated

      const newUsersResult = await this.createNewParticipants()
      occurredError = occurredError || newUsersResult.error
      hasUpdates = hasUpdates || <newUsersResult class="updated"></newUsersResult>

      const orderResult = await this.updateParticipantsOrder()
      occurredError = occurredError || orderResult.error
      hasUpdates = hasUpdates || orderResult.updated

      return { occurredError, hasUpdates }
    },
    async updateOwnerSignature() {
      if (this.isOwnerSignatureRequired === this.requiredOwnerSignature) {
        return { error: false, updated: false }
      }

      try {
        await this.$store.dispatch('participants/update', {
          id: this.owner.id,
          payload: { isSignatureRequired: this.requiredOwnerSignature }
        })
        return { error: false, updated: true }
      } catch (e) {
        this.$handlers.error(e, this)
        return { error: true, updated: false }
      }
    },
    async processDeletedParticipants() {
      const deletedParticipantIds = Object.keys(this.localDeletedParticipants)
      if (deletedParticipantIds.length === 0) {
        return { error: false, updated: false }
      }
      try {
        const deletePromises = deletedParticipantIds.map(participantId =>
          this.$store.dispatch('participants/delete', { id: participantId })
        )
        await Promise.all(deletePromises)
        deletedParticipantIds.forEach(participantId => {
          this.$delete(this.localDeletedParticipants, participantId)
        })
        return { error: false, updated: true }
      } catch (e) {
        this.$handlers.error(e, this, true)
        return { error: true, updated: false }
      }
    },
    async processChangedParticipants() {
      const changedParticipantIds = Object.keys(this.localParticipantChanges)
      if (changedParticipantIds.length === 0) {
        return { error: false, updated: false }
      }

      let hasErrors = false
      let hasUpdates = false

      for (const participantId of changedParticipantIds) {
        const result = await this.recreateParticipant(participantId)
        hasErrors = hasErrors || result.error
        hasUpdates = hasUpdates || result.updated
      }

      return { error: hasErrors, updated: hasUpdates }
    },
    async recreateParticipant(participantId) {
      try {
        const storeParticipant = this.participants.find(p => p.id === participantId)
        if (!storeParticipant) {
          throw new Error(`Participant ${participantId} not found in store`)
        }

        const localChange = this.localParticipantChanges[participantId]
        const newParticipantPayload = this.buildParticipantPayload(
          storeParticipant,
          localChange.role
        )

        await this.$store.dispatch('participants/delete', { id: participantId })

        const createResult = await this.$store.dispatch('participants/createDocumentParticipant', {
          documentId: this.document.id,
          payload: newParticipantPayload,
          saveItems: true
        })

        this.$delete(this.localParticipantChanges, participantId)
        this.updateOrderIdIfNeeded(participantId, createResult?.id)

        return { error: false, updated: true }
      } catch (e) {
        this.$handlers.error(e, this)
        return { error: true, updated: false }
      }
    },
    buildParticipantPayload(storeParticipant, role) {
      const payload = {
        action: role,
        priority: storeParticipant.priority || 0
      }

      if (storeParticipant.user?.email) {
        payload.email = storeParticipant.user.email
      } else if (storeParticipant.edrpou) {
        payload.edrpou = storeParticipant.edrpou
      } else if (storeParticipant.organization?.edrpou) {
        payload.edrpou = storeParticipant.organization.edrpou
      } else {
        throw new Error(`Cannot determine participant type for ${storeParticipant.id}`)
      }

      return payload
    },
    updateOrderIdIfNeeded(oldId, newId) {
      if (this.localParticipantsOrder[oldId] !== undefined) {
        const oldPriority = this.localParticipantsOrder[oldId]
        this.$delete(this.localParticipantsOrder, oldId)
        this.$set(this.localParticipantsOrder, newId, oldPriority)
      }
    },
    async createNewParticipants() {
      if (this.newUsers.length === 0) {
        return { error: false, updated: false }
      }

      let hasErrors = false
      let hasUpdates = false

      for (const user of this.newUsers) {
        const result = await this.createSingleParticipant(user)
        hasErrors = hasErrors || result.error
        hasUpdates = hasUpdates || result.updated
      }

      return { error: hasErrors, updated: hasUpdates }
    },
    async createSingleParticipant(user) {
      try {
        const userCopy = this.prepareUserPayload(user)
        await this.$store.dispatch('participants/createDocumentParticipant', {
          documentId: this.document.id,
          payload: userCopy,
          saveItems: false
        })
        return { error: false, updated: true }
      } catch (e) {
        this.$handlers.error(e, this)
        return { error: true, updated: false }
      }
    },
    prepareUserPayload(user) {
      const userCopy = { ...user }
      delete userCopy.id
      if (!userCopy.name) delete userCopy.name

      if (!userCopy.action && userCopy.role) {
        userCopy.action =
          this.participantsRoleActionMap[userCopy.role] || this.participantActionsMap.SIGN
      }

      userCopy.priority =
        this.localParticipantsOrder[user.id] !== undefined
          ? this.localParticipantsOrder[user.id]
          : user.priority

      return userCopy
    },
    async updateParticipantsOrder() {
      const hasOrderChanges = Object.keys(this.localParticipantsOrder).length
      if (!hasOrderChanges) {
        return { error: false, updated: false }
      }

      try {
        const newUserIds = this.newUsers.map(u => u.id)

        const orderUpdatePromises = Object.keys(this.localParticipantsOrder)
          .filter(participantId => !newUserIds.includes(participantId))
          .map(participantId => {
            const newPriority = this.localParticipantsOrder[participantId]
            return this.$store.dispatch('participants/update', {
              id: participantId,
              payload: { priority: newPriority }
            })
          })

        if (orderUpdatePromises.length > 0) {
          await Promise.all(orderUpdatePromises)
        }

        this.localParticipantsOrder = {}
        return { error: false, updated: orderUpdatePromises.length > 0 }
      } catch (e) {
        this.$handlers.error(e, this)
        return { error: true, updated: false }
      }
    },
    async handleSubmitResult({ occurredError, hasUpdates }) {
      const hasChangedParticipants = Object.keys(this.localParticipantChanges).length
      const hasNewUsers = this.newUsers.length > 0
      const hasOrderChanges = Object.keys(this.localParticipantsOrder).length
      const hasDeletedParticipants = Object.keys(this.localDeletedParticipants).length
      const hasNotLaunchedParticipantsRoute = !this.isDocumentSent && this.participants.length
      const hasAnyUpdates =
        hasUpdates ||
        hasChangedParticipants ||
        hasNewUsers ||
        hasOrderChanges ||
        hasDeletedParticipants ||
        hasNotLaunchedParticipantsRoute

      if (hasAnyUpdates && !occurredError) {
        if (this.isDocumentSent) {
          await this.handleSentDocumentUpdate()
        } else {
          await this.handleNewDocumentSend()
        }
        return
      }

      this.closeDialogOrDrawer(hasAnyUpdates && !occurredError)
    },
    handleSentDocumentUpdate() {
      this.$notification.success(this.$t('Changes saved successfully'))
      this.closeDialogOrDrawer(true)
    },
    async handleNewDocumentSend() {
      try {
        await this.sendDocument()
        this.updateDocumentLocally({ state: DOCUMENT_STATES.SENT })

        window.dataLayer?.push({
          event: 'document_sent',
          email: this.$User?.email
        })

        this.closeDialogOrDrawer(true)
        await this.showSuccessDialog()
      } catch (e) {
        this.$handlers.error(e, this)
      }
    },
    async sendDocument() {
      if (this.$store._actions['documents/send']) {
        await this.$store.dispatch('documents/send', { id: this.document.id })
      } else if (this.$store._actions['documents/sendToParticipants']) {
        await this.$store.dispatch('documents/sendToParticipants', this.document.id)
      } else if (this.$store._actions['documents/update']) {
        await this.$store.dispatch('documents/update', {
          id: this.document.id,
          payload: { state: DOCUMENT_STATES.SENT }
        })
      }
    },
    async showSuccessDialog() {
      await this.contentDialog.open({
        width: '512px',
        component: 'block-documents-success',
        componentProps: {
          title: this.$t('Document has sent'),
          text: this.$t(
            'All participants will receive a notification by mail about the need to process the document. As soon as they take action on the document, we will notify you instantly.'
          ),
          isInformationDialog: true
        }
      })
    },
    closeDialogOrDrawer(success) {
      if (this.xsDevice) {
        this.closeDrawer(success)
      } else {
        this.closeModal(success)
      }
    },
    async handlePublicLinkToggle(value) {
      this.enablePublicLink = value
      this.viewMode = value ? 'public' : 'recipients'

      if (value) {
        await this.generatePublicLinkForDisplay()
      } else {
        await this.deleteLink()
      }
    },
    async generatePublicLink() {
      this.copyLoading = true
      try {
        const response = await this.$store.dispatch('documents/generateDocumentLink', {
          id: this.document.id,
          payload: { action: this.publicAction }
        })
        return response?.data?.link
      } catch (e) {
        this.$handlers.error(e, this)
        return null
      } finally {
        this.copyLoading = false
      }
    },
    async generatePublicLinkForDisplay() {
      const link = await this.generatePublicLink()
      if (link) {
        this.publicLinkUrl = link
        this.updateDocumentLocally({ hasPublicAccess: true })
      }
    },
    onPublicLinkCopied() {
      this.hasCopiedLink = true
      this.updateDocumentLocally({ state: DOCUMENT_STATES.SENT })
      window.dataLayer?.push({
        event: 'copy_link',
        email: this.$User?.email
      })
    },
    async deleteLink() {
      try {
        await this.$store.dispatch('documents/deleteDocumentLink', this.document.id)
        this.updateDocumentLocally({ hasPublicAccess: false })
        this.publicLinkUrl = ''
      } catch (e) {
        this.$handlers.error(e, this)
      }
    },
    updateDocumentLocally(data) {
      this.$store.commit('documents/UPDATE_DOCUMENT', { id: this.document.id, data })
    },
    async copyPublicLink() {
      const link = await this.generatePublicLink()
      if (!link) return

      this.updateDocumentLocally({ hasPublicAccess: true })
      if (this.xsDevice || this.smDevice || isSafari) {
        await this.contentDialogContentTop.open({
          component: 'm-block-input-copy',
          width: '512px',
          componentProps: {
            title: this.$t('Copy public link'),
            value: link
          },
          componentListeners: {
            copied: () => this.onPublicLinkCopied()
          }
        })
      } else {
        this.copy(link)
        this.onPublicLinkCopied()
        this.$notification.success(this.$t('Copied to clipboard'))
      }
    },
    async updatePublicLinkAction() {
      const link = await this.generatePublicLink()
      if (link) {
        this.publicLinkUrl = link
      }
    },
    deleteParticipant(participant) {
      if (participant.isNew) {
        this.newUsers = this.newUsers.filter(user => user.id !== participant.id)
      } else {
        this.$set(this.localDeletedParticipants, participant.id, true)
        if (this.localParticipantChanges[participant.id]) {
          this.$delete(this.localParticipantChanges, participant.id)
        }
        if (this.localParticipantsOrder[participant.id] !== undefined) {
          this.$delete(this.localParticipantsOrder, participant.id)
        }
      }
    },
    getParticipantCurrentAction(participant) {
      if (participant.isNew) {
        return this.formattedParticipantActions.find(action => action.value === participant.action)
      }

      const localChange = this.localParticipantChanges[participant.id]
      if (localChange) {
        return this.formattedParticipantActions.find(a => a.value === localChange.role)
      }

      const storeParticipant = this.participants.find(p => p.id === participant.id)
      const role = storeParticipant?.role || participant.role
      const actionValue = this.participantsRoleActionMap[role]
      return (
        this.formattedParticipantActions.find(a => a.value === actionValue) ||
        this.formattedParticipantActions[0]
      )
    },
    handleParticipantActionChange(participant, newRole) {
      if (participant.isNew) {
        const newUser = this.newUsers.find(user => user.id === participant.id)
        if (newUser) {
          this.$set(newUser, 'role', newRole)
          this.$set(newUser, 'action', this.participantsRoleActionMap[newRole] || newUser.action)
        }
        return
      }
      if (participant.id) {
        this.$set(this.localParticipantChanges, participant.id, {
          role: newRole,
          changedRole: true
        })
      }
    },
    handleParticipantAdded(newParticipant) {
      this.newUsers.push(newParticipant)
    },
    getParticipantName(p) {
      return p.edrpou ? p.organization?.name : p.user?.email
    },
    getCurrentActionLabel(participant) {
      return this.getParticipantCurrentAction(participant)?.text || this.$t('View')
    },
    startReorderMode() {
      this.isReorderMode = true
    },
    saveReorderChanges() {
      this.isReorderMode = false
    },
    onFlipParticipantsEnd() {
      this.drag = false
    }
  }
}
</script>

<style scoped lang="scss">
.document-share-pro {
  display: flex;
  flex-direction: column;
  height: 100%;
  max-height: 90vh;
  background: $white;
  &__header-section {
    background: $background-gray;
    padding: 40px 40px 0 40px;
    flex-shrink: 0;
    position: relative;
  }
  &__header {
    position: relative;
    display: flex;
    align-items: center;
    justify-content: space-between;
  }
  &__main-content {
    flex: 1;
    overflow-y: auto;
    display: flex;
    flex-direction: column;
  }
  &__content {
    flex: 1;
    display: flex;
    flex-direction: column;
  }
  &__input-section {
    background: $background-gray;
    padding: 40px 40px 20px 40px;
    flex-shrink: 0;
  }
  &__recipients {
    position: relative;
    display: flex;
    flex-direction: column;
    flex: 1;
  }
  &__public-link-actions {
    display: flex;
    justify-content: space-between;
    padding: 32px 40px;
  }
  &__loading {
    height: 400px;
    display: flex;
    align-items: center;
    justify-content: center;
  }
  &__participants-wrapper {
    flex: 1;
    padding: 0 40px;
  }
  &__participants {
    padding-top: 32px;
  }
  &__participants-header {
    display: flex;
    align-items: center;
    justify-content: space-between;
  }
  &__change-order-btn {
    color: $border-primary !important;
  }
  &__footer {
    display: flex;
    align-items: center;
    justify-content: space-between;
    flex-shrink: 0;
    padding: 40px;
    &-left {
      display: flex;
      flex-direction: column;
      align-items: flex-start;
    }
    &-right {
      display: flex;
      gap: 16px;
    }
  }
  &__public-link-container {
    display: flex;
    align-items: center;
    gap: 12px;
  }
  &__public-link-url {
    display: flex;
    height: 48px;
    padding: 12px 16px;
    align-items: center;
    gap: 12px;
    flex: 1;
    border-radius: 12px;
    border: 1px solid #f1f1f1;
    background: $white;
  }
  &__public-link-text {
    overflow: hidden;
    color: $dark2;
    text-overflow: ellipsis;
    font-size: 14px;
    font-weight: 400;
    line-height: 22px;
    white-space: nowrap;
    flex: 1;
  }
  &__copy-button {
    height: 40px;
    text-transform: none;
    font-weight: 500;
  }
  &__public-link-toggle {
    display: flex;
    align-items: center;
  }
  &__public-action-select {
    max-width: 170px;
  }
  &__participants-add {
    display: flex;
    align-items: center;
    width: 100%;
    &-content {
      display: flex;
      align-items: center;
      flex-grow: 1;
      width: 100%;
      position: relative;
    }
    &-action {
      width: 100%;
      position: relative;
      max-width: 170px;
    }
  }
  &__participants-icon {
    width: 48px;
    height: 48px;
    border-radius: $btn-border-radius;
    display: flex;
    align-items: center;
    justify-content: center;
    margin-right: 16px;
    flex-shrink: 0;
    background: $success;
    color: $white;
  }
  &__participants-content {
    display: flex;
    flex-direction: column;
    gap: 4px;
    flex-grow: 1;
  }
  &__public {
    display: flex;
    flex-direction: column;
    flex: 1;
  }
}
</style>
